// src/components/JarvislyModule/PageHeader/ActionsButton.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Affix, Flex, Grid, theme } from 'antd';
import FilterButton from './FilterButton';
import RefreshButton from './RefreshButton';
import ParametersButton from './ParametersButton';
import GoBackButton from './GoBackButton';
import ActionButton from './ActionButton';
import React, { useMemo, useRef, useState } from 'react';
import utils from 'utils/utils';
import {
  NAV_TYPE_TILE,
  NAV_TYPE_TOP,
  SIDE_NAV_COLLAPSED_WIDTH,
  SIDE_NAV_WIDTH,
} from 'constants/ThemeConstant';
import { DEFAULT_BACK_COLOR, DEFAULT_BACK_COLOR_DARK } from 'constants/Constants';
import Icon from 'components/JarvislyComponents/Icon';
import translateX from 'utils/translateX';
import { useDocumentContext } from 'components/core/DocumentProvider';
import EditButton from 'layouts/Pages/PageHeader/EditButton';
import RemoveButton from 'layouts/Pages/PageHeader/RemoveButton';
import DropdownButtonMenu from 'layouts/Pages/PageHeader/DropdownButtonMenu';
import { HomeOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import authService from 'services/authService';
import useZustand from 'hooks/useZustand';
import { useModuleContext } from 'components/core/ModuleProvider';
import SimpleSelectCompany from 'components/SimpleComponents/SimpleSelectCompany';
import ViewSelector from './ViewSelector';
import app from 'configs/appResolver';
import './ActionsButton.less';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const { useBreakpoint } = Grid;

// Floating style adjustments (only applied on desktop, mobile always uses full-width centered)
// Fine-tune horizontal offset (positive = move left, negative = move right)
const FLOATING_OFFSET_ADJUSTMENT = -1;
// Fine-tune vertical offset
const FLOATING_TOP_ADJUSTMENT = 0;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
export const ActionsButton = ({
  affixWhen = 'none',
  backgroundColor,
  marginRight = 8,
  showTitle = false,
  locate = 'pageHeader', // 'pageHeader', 'pageSettings' or 'pageTab'
  extraActionContext = {},
  hideHome = false,
  leftContent,
  appendButtons,
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const moduleContext = useModuleContext();
  const { tab, viewMode: mode } = useDocumentContext();
  const { listContext } = moduleContext;

  const storedModule = useZustand('module', 'config');
  const storedCurrentPage = useZustand('module', 'currentPage');
  const storedCurrentRoute = useZustand('app', 'currentRoute');
  const storedPageSettings = useZustand('module', 'pageSettings');
  const storedCompanies = useZustand('auth', 'companies');
  const storedSubscription = useZustand('auth', 'subscription');

  const navCollapsed = useZustand('theme', 'navCollapsed');
  const navType = useZustand('theme', 'navType');
  const storedCurrentTheme = useZustand('theme', 'currentTheme');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const location = useLocation();
  const navigate = useNavigate();
  const { token } = theme.useToken();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const screens = useBreakpoint();
  const isMobile = !utils.getBreakPoint(screens).includes('sm');
  const isTopNavVisible = screens.lg; // TopNav hidden below 992px (TABLET breakpoint)
  const navTypeLower = navType?.toLowerCase?.() || '';
  const isNavTile = navTypeLower === NAV_TYPE_TILE.toLowerCase();
  const isNavTop = navTypeLower === NAV_TYPE_TOP.toLowerCase();
  const title = storedModule?.title || storedCurrentRoute?.title;
  const icon = storedModule?.icon || storedCurrentRoute?.icon;
  // resolve current tabFromUrl: context -> URL ?tabFromUrl= -> initTab from config
  const urlTab = useMemo(() => {
    const qs = new URLSearchParams(location.search);
    return qs.get('tab') || undefined;
  }, [location.search]);

  const fallbackInitTab = storedCurrentPage?.settings?.initialTab;
  const currentTab = tab ?? urlTab ?? fallbackInitTab;

  // central passthrough context to children buttons
  const actionContext = useMemo(
    () => ({
      navigate,
      currentTab,
      isMobile,
      authService,
      ...extraActionContext,
    }),
    [navigate, currentTab, extraActionContext, isMobile, authService]
  );

  const actionsDef = useMemo(() => {
    if (locate === 'pageHeader') {
      return storedCurrentPage?.header?.actions || storedCurrentPage?.listSettings?.actions;
    } else if (locate === 'pageSettings') {
      return storedCurrentPage?.settings?.actions;
    } else {
      return storedCurrentPage?.tabs?.[currentTab]?.actions;
    }
  }, [locate, storedCurrentPage, currentTab]);

  // normalizar actions em runtime (primeira passada)
  const normalized = useMemo(
    () => normalizeActions(actionsDef, actionContext),
    [actionsDef, actionContext]
  );

  // congelar actions para evitar churn de identidade entre renders
  const actionsRef = useRef(normalized);
  const actions = useMemo(() => {
    if (!deepEqualActions(actionsRef.current, normalized)) {
      actionsRef.current = normalized;
    }
    return actionsRef.current;
  }, [normalized]);

  const showActionsButtons = useMemo(() => {
    return computeShowActionsButtons({
      locate,
      page: storedCurrentPage,
      docSettings: storedPageSettings,
      tab: currentTab,
      actions,
    });
  }, [locate, storedCurrentPage, storedPageSettings, currentTab, actions]);

  // Check for a unified 'editRefresh' action (composite button that alternates)
  const hasEditRefresh = isActionVisible('editRefresh', actions);

  const showNew = isActionVisible('new', actions);

  // Standalone 'edit' button: always visible when declared, disabled when mode !== 'view'
  const showEdit = isActionVisible('edit', actions);
  const editDisabled = mode !== 'view';

  // Standalone 'refresh' button: always visible and enabled when declared
  const showRefresh = isActionVisible('refresh', actions);

  const showSave = isActionVisible('save', actions);
  const showReturn = isActionVisible('return', actions);
  const showFilter = isActionVisible('filter', actions);
  const showParameters = isActionVisible('parameters', actions);
  // For dropdownMenu, also check hideButton function with storedSubscription
  const showDropdownMenu = useMemo(() => {
    if (!isActionVisible('dropdownMenu', actions)) return false;
    const { config } = resolveAction('dropdownMenu', actions);
    if (typeof config?.hideButton === 'function') {
      return !config.hideButton({ storedSubscription });
    }
    return !config?.hideButton;
  }, [actions, storedSubscription]);
  const showFilterByCompany = storedCurrentPage?.settings?.filterByCompany === true;

  // Home button: show on the module's initial page, or when page config has forceShowHome
  const showHomeButton =
    !hideHome &&
    (storedCurrentPage?.name === storedModule?.initialPage ||
      storedCurrentPage?.header?.forceShowHome === true);

  // leftContent: prefer prop, fall back to config (rendered on left side of toolbar)
  const resolvedLeftContent = leftContent ?? storedCurrentPage?.header?.leftContent;

  const newActionProps = getActionProps('new', actions);

  // Props for standalone buttons
  const editActionProps = getActionProps('edit', actions);
  const refreshActionProps = getActionProps('refresh', actions);

  // Props for composite editRefresh button
  const editRefreshActionProps = getActionProps('editRefresh', actions);

  const showRemove = isActionVisible('remove', actions);
  const removeActionProps = getActionProps('remove', actions);

  const saveActionProps = getActionProps('save', actions);
  const returnActionProps = getActionProps('return', actions);
  const filterActionProps = getActionProps('filter', actions);
  const parametersActionProps = getActionProps('parameters', actions);
  const dropdownMenuActionProps = getActionProps('dropdownMenu', actions);

  // Custom actions: any action object whose key is NOT a standard built-in type
  const STANDARD_ACTIONS = ['new', 'save', 'edit', 'refresh', 'editRefresh', 'return', 'filter', 'parameters', 'dropdownMenu', 'remove'];
  const customActions = useMemo(() => {
    if (!Array.isArray(actions)) return [];
    return actions
      .filter(a => {
        if (!a || typeof a !== 'object') return false;
        const key = a.action || a.name || a.type;
        return key && !STANDARD_ACTIONS.includes(key) && a.visible !== false;
      })
      .map(a => ({
        key: a.action || a.name || a.type,
        ...getActionProps(a.action || a.name || a.type, actions),
      }));
  }, [actions]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [isAffixed, setIsAffixed] = useState(false);
  // State to control animation - only true briefly when affix first triggers
  const [shouldPlayAnimation, setShouldPlayAnimation] = useState(false);
  // Store the measured right offset from viewport to maintain button position
  const [measuredRightOffset, setMeasuredRightOffset] = useState(null);
  // State to hide toolbar during navigation transition
  const [isNavigatingAway, setIsNavigatingAway] = useState(false);
  // Ref to measure the toolbar position
  const toolbarRef = useRef(null);

  // Update actionContext to include setIsAffixed for buttons that need to reset it
  const actionContextWithAffix = useMemo(
    () => ({
      ...actionContext,
      setIsAffixed,
      setIsNavigatingAway,
    }),
    [actionContext]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Reset all affix states when route changes
  React.useEffect(() => {
    setIsAffixed(false);
    setShouldPlayAnimation(false);
    setMeasuredRightOffset(null);
    setIsNavigatingAway(false);
  }, [location.pathname]);

  // Measure the toolbar's right offset - runs on mount and when conditions change
  React.useLayoutEffect(() => {
    const measureOffset = () => {
      if (toolbarRef.current && !isAffixed) {
        const rect = toolbarRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        // Calculate distance from the right edge of the toolbar to the right edge of viewport
        const rightOffset = viewportWidth - rect.right;
        setMeasuredRightOffset(rightOffset);
      }
    };

    // Immediate measurement
    measureOffset();

    // Also measure after a short delay to ensure DOM is fully rendered (fixes F5 + quick scroll issue)
    const initialTimer = setTimeout(measureOffset, 100);

    // Re-measure on window resize
    window.addEventListener('resize', measureOffset);
    return () => {
      window.removeEventListener('resize', measureOffset);
      clearTimeout(initialTimer);
    };
  }, [isAffixed, navCollapsed]); // Re-measure when nav collapses/expands

  // Additional mount measurement to catch initial render
  React.useEffect(() => {
    const measureOnMount = () => {
      if (toolbarRef.current && !isAffixed) {
        const rect = toolbarRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const rightOffset = viewportWidth - rect.right;
        if (rightOffset > 0) {
          setMeasuredRightOffset(rightOffset);
        }
      }
    };
    // Run after initial paint
    requestAnimationFrame(measureOnMount);
  }, []); // Only on mount

  // Handle animation: play once when isAffixed becomes true, then stop
  React.useEffect(() => {
    if (isAffixed) {
      // Trigger animation
      setShouldPlayAnimation(true);
      // After animation completes, stop it to prevent re-running on re-renders
      const timer = setTimeout(() => {
        setShouldPlayAnimation(false);
      }, 400); // slightly longer than animation duration (350ms)
      return () => clearTimeout(timer);
    } else {
      setShouldPlayAnimation(false);
    }
  }, [isAffixed]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const getNavWidth = () => {
    if (isNavTop || isNavTile || isMobile) return '0px';
    if (navCollapsed) return `${SIDE_NAV_COLLAPSED_WIDTH}px`;
    return `${SIDE_NAV_WIDTH}px`;
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II (hooks that must run before early return)
  // ─────────────────────────────────────────────────────────────────────────────
  // Calculate offsetTop based on navigation type
  // TopNav: topbar (70px) + topnav (55px) = 125px
  // SideNav/Tile: topbar (70px) only
  // Mobile: topbar (70px) only
  const affixOffsetTop = useMemo(() => {
    if (isMobile || !storedSubscription?._id) return 70;
    if (isNavTop && isTopNavVisible) return 70 + 55; // topbar + topnav (desktop only)
    return 70; // sidenav, tile, or topnav on tablet (topnav hidden via CSS)
  }, [isMobile, isNavTop, isTopNavVisible, storedSubscription]);

  // Determine if Affix should be enabled
  const shouldAffix =
    affixWhen === 'always' ||
    (affixWhen === 'mobile' && isMobile) ||
    (affixWhen === 'desktop' && !isMobile);

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  // Hide toolbar during navigation transition to prevent visual glitch
  if (isNavigatingAway) return null;
  if (!showActionsButtons) return null;

  // Desktop: floating style with offset adjustment
  // Mobile: full-width centered style without offset
  const useFloatingStyle = !isMobile;

  // Apply offset adjustment only on desktop (floating style)
  const rightOffset = useFloatingStyle
    ? (measuredRightOffset ?? 24) + FLOATING_OFFSET_ADJUSTMENT
    : 0;

  // Floating style (DESKTOP): compact bar aligned to right with rounded corners and shadow
  const floatingWrapperStyle = {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
    marginBottom: listContext ? 22 : 0,
  };

  const floatingToolbarStyle = {
    marginTop: FLOATING_TOP_ADJUSTMENT || 0,
    display: 'flex',
    flexWrap: 'nowrap',
    justifyContent: resolvedLeftContent ? 'space-between' : 'flex-end',
    alignItems: 'center',
    width: resolvedLeftContent && !isAffixed ? '100%' : undefined,
    // Use base background color when affixed (DEFAULT_BACK_COLOR_DARK in dark mode, DEFAULT_BACK_COLOR in light mode)
    backgroundColor: isAffixed
      ? storedCurrentTheme === 'dark'
        ? DEFAULT_BACK_COLOR_DARK
        : DEFAULT_BACK_COLOR
      : 'unset',
    padding: 8,
    minWidth: resolvedLeftContent ? undefined : 'fit-content',
    // When affixed, use fixed positioning with measured offset to maintain exact position
    position: isAffixed ? 'fixed' : 'relative',
    right: isAffixed ? rightOffset : 'auto',
    borderRadius: isAffixed ? 8 : 0,
    boxShadow: isAffixed ? '0 4px 16px rgba(0, 0, 0, 0.12), 0 2px 4px rgba(0, 0, 0, 0.08)' : 'none',
    border: isAffixed ? `1px solid ${token.colorBorderSecondary || 'rgba(0, 0, 0, 0.06)'}` : 'none',
    transition: 'box-shadow 0.3s ease, border 0.3s ease, background-color 0.3s ease',
    animation: shouldPlayAnimation ? 'fadeInDown 0.35s ease-out forwards' : 'none',
  };

  // Full-width style (MOBILE): bar spans entire width with centered buttons
  const fullWidthWrapperStyle = {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    marginBottom: listContext ? 22 : 0,
  };

  const isToolbarColumn = !!resolvedLeftContent && isMobile;

  const fullWidthToolbarStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: isToolbarColumn ? 'column' : 'row',
    justifyContent: resolvedLeftContent ? (isMobile ? 'flex-start' : 'space-between') : 'center',
    // stretch: makes leftContent + actionsGroup fill full toolbar width so left edges align
    alignItems: isToolbarColumn ? 'stretch' : 'center',
    gap: 8,
    backgroundColor: isAffixed
      ? backgroundColor ||
        (storedCurrentTheme === 'dark' ? DEFAULT_BACK_COLOR_DARK : DEFAULT_BACK_COLOR)
      : 'unset',
    padding: isAffixed ? '8px 15px' : 8,
    // No offset adjustment on mobile - just centered
    position: isAffixed ? 'fixed' : 'relative',
    left: isAffixed ? 0 : 'auto',
    right: isAffixed ? 0 : 'auto',
    boxShadow: isAffixed ? '0 2px 8px rgba(0, 0, 0, 0.15)' : 'none',
    borderBottom: isAffixed ? '1px solid rgba(0, 0, 0, 0.08)' : 'none',
    minWidth: 'fit-content',
    // Limit max width on mobile to respect parent padding when not affixed
    maxWidth: isAffixed ? '100%' : 'calc(100% - 30px)',
    transition: 'box-shadow 0.3s ease, border-bottom 0.3s ease, background-color 0.3s ease',
    animation: shouldPlayAnimation ? 'fadeInDown 0.35s ease-out forwards' : 'none',
  };

  // Select styles based on screen size: floating for desktop, full-width for mobile
  const wrapperStyle = useFloatingStyle ? floatingWrapperStyle : fullWidthWrapperStyle;
  const toolbarStyle = useFloatingStyle ? floatingToolbarStyle : fullWidthToolbarStyle;

  const leftContentWrapperStyle = {
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
    flexShrink: 1,
    minWidth: 0,
  };

  const isColumnLayout = isMobile && showFilterByCompany && storedCompanies?.length > 1;
  const actionsGroupStyle = {
    display: 'flex',
    flexDirection: isColumnLayout ? 'column' : 'row',
    flexWrap: 'nowrap',
    justifyContent: isMobile ? 'center' : 'flex-end',
    alignItems: isColumnLayout ? 'flex-start' : 'center',
    gap: 8,
    minWidth: 'fit-content',
    // When toolbar is column (leftContent + mobile), fill full width to match leftContent left edge
    width: isToolbarColumn ? '100%' : undefined,
  };

  const companyWrapperStyle = {
    marginBottom: isMobile ? 8 : 0,
    width: isColumnLayout ? '100%' : undefined,
  };

  const actionButtonsContainerStyle = {
    display: 'flex',
    flexWrap: 'nowrap',
    justifyContent: isMobile ? 'center' : 'flex-end',
    alignItems: 'center',
    gap: 8,
    // Fill full width when stacked so buttons are centered within the available space
    width: isColumnLayout ? '100%' : undefined,
  };

  const toolbarContent = (
    <>
      {/* Wrapper for toolbar positioning */}
      <div style={wrapperStyle}>
        <div ref={toolbarRef} style={toolbarStyle}>
          {resolvedLeftContent ? (
            <div style={leftContentWrapperStyle}>
              {resolvedLeftContent}
            </div>
          ) : showTitle && isAffixed && !isMobile ? (
            <div></div>
          ) : (
            <div></div>
          )}

          <div
            className="force-padding-inline-12"
            style={actionsGroupStyle}
          >
            {/* SelectCompany - on mobile, appears on its own row above action buttons */}
            {showFilterByCompany && storedCompanies?.length > 1 && (
              <div style={companyWrapperStyle}>
                <SimpleSelectCompany
                  key="company-select"
                  className={isMobile ? 'actions-button-company-select-mobile' : undefined}
                  fullWidth={isColumnLayout}
                />
              </div>
            )}

            {/* Action buttons container - on mobile, appears below SelectCompany */}
            <div style={actionButtonsContainerStyle}>
              {showHomeButton ? (
                <GoBackButton
                  show
                  onClick={() => navigate(app.AUTHENTICATED_ENTRY)}
                  label="home"
                  icon={<HomeOutlined />}
                  actionContext={actionContextWithAffix}
                />
              ) : null}

              {/* View Selector (init / dashboard / reports) */}
              <ViewSelector />

              {showFilter ? (
                <FilterButton
                  actions={actions}
                  actionContext={actionContextWithAffix}
                  {...filterActionProps}
                />
              ) : null}

              {/* Standalone Edit Button - sempre visível, disabled quando mode !== 'view' */}
              {showEdit ? (
                <EditButton
                  actions={actions}
                  actionContext={actionContextWithAffix}
                  {...editActionProps}
                  disabled={editDisabled}
                />
              ) : null}

              {/* Standalone Refresh Button - sempre visível e habilitado */}
              {showRefresh ? (
                <RefreshButton
                  actions={actions}
                  actionContext={actionContextWithAffix}
                  {...refreshActionProps}
                />
              ) : null}

              {/* Composite EditRefresh Button - alterna entre Edit e Refresh baseado no mode */}
              {hasEditRefresh ? (
                mode === 'view' ? (
                  <EditButton
                    actions={actions}
                    actionContext={actionContextWithAffix}
                    {...editRefreshActionProps}
                  />
                ) : (
                  <RefreshButton
                    actions={actions}
                    actionContext={actionContextWithAffix}
                    {...editRefreshActionProps}
                  />
                )
              ) : null}

              {showParameters ? (
                <ParametersButton
                  actions={actions}
                  actionContext={actionContextWithAffix}
                  {...parametersActionProps}
                />
              ) : null}
              {/* Custom actions (non-standard) rendered before return button */}
              {customActions.map(ca => (
                <ActionButton
                  key={ca.key}
                  actions={actions}
                  actionContext={actionContextWithAffix}
                  {...ca}
                />
              ))}
              {showRemove ? (
                <RemoveButton
                  actions={actions}
                  actionContext={actionContextWithAffix}
                  {...removeActionProps}
                />
              ) : null}
              {showReturn ? (
                <GoBackButton
                  actions={actions}
                  actionContext={actionContextWithAffix}
                  {...returnActionProps}
                />
              ) : null}
              {showNew ? (
                <ActionButton
                  actions={actions}
                  actionContext={actionContextWithAffix}
                  {...newActionProps}
                />
              ) : null}
              {showSave ? (
                <ActionButton
                  disabled={!(mode === 'edit' || mode === 'add')}
                  actions={actions}
                  actionContext={actionContextWithAffix}
                  {...saveActionProps}
                />
              ) : null}
              {showDropdownMenu ? (
                <DropdownButtonMenu
                  actions={actions}
                  actionContext={actionContextWithAffix}
                  {...dropdownMenuActionProps}
                />
              ) : null}
              {appendButtons}
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return shouldAffix ? (
    <>
      {/* CSS keyframes for smooth animation */}
      <style>
        {`
          @keyframes slideDownFadeIn {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes fadeInDown {
            0% {
              opacity: 0;
              transform: translate3d(0, -100%, 0);
            }
            100% {
              opacity: 1;
              transform: translate3d(0, 0, 0);
            }
          }
        `}
      </style>
      <Affix
        offsetTop={affixOffsetTop}
        onChange={affixed => {
          setIsAffixed(prev => (prev === affixed ? prev : affixed));
        }}
        className={resolvedLeftContent ? 'actions-button-affix-full-width' : undefined}
      >
        {toolbarContent}
      </Affix>
    </>
  ) : (
    toolbarContent
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ActionsButton;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function computeShowActionsButtons({ locate, page, docSettings, tab, actions }) {
  if (locate === 'pageHeader' && page?.header?.showActionsButton !== undefined) {
    return !!page?.header?.showActionsButton;
  }
  if (locate === 'pageSettings' && docSettings?.showActionsButton !== undefined) {
    return !!docSettings.showActionsButton;
  }
  if (locate === 'pageTab') {
    const flag = page?.tabs?.[tab]?.showActionsButton;
    if (typeof flag === 'boolean') return flag;
  }
  return Array.isArray(actions) && actions.length > 0;
}

function normalizeActions(actionsDef, ctx) {
  const list = [];

  const pushOne = item => {
    if (!item) return;
    if (typeof item === 'function') {
      const res = safeCall(item, ctx);
      if (Array.isArray(res)) res.forEach(pushOne);
      else if (res) pushOne(res);
      return;
    }
    if (typeof item === 'string') return list.push(item);
    if (typeof item === 'object') return list.push(item);
  };

  let base = actionsDef;
  if (typeof actionsDef === 'function') base = safeCall(actionsDef, ctx);

  if (Array.isArray(base)) base.forEach(pushOne);
  else pushOne(base);

  const seen = new Set();
  return list.filter(it => {
    const key =
      typeof it === 'string' ? it : it?.action || it?.name || it?.type || JSON.stringify(it);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function resolveAction(key, actions = []) {
  if (!Array.isArray(actions)) return { visible: false, config: null };
  const obj =
    actions.find(
      a => a && typeof a === 'object' && (a.action === key || a.name === key || a.type === key)
    ) || null;
  if (obj) return { visible: obj.visible !== false, config: obj };
  const strPresent = actions.some(a => typeof a === 'string' && a === key);
  return { visible: strPresent, config: null };
}

function isActionVisible(key, actions) {
  const { visible } = resolveAction(key, actions);
  return !!visible;
}

function getActionProps(key, actions) {
  const { config } = resolveAction(key, actions);
  if (!config) return {};
  const {
    label,
    icon,
    tooltip,
    disabled,
    onClick,
    color,
    type,
    hideButton,
    dropdownIcon,
    dropdownTooltip,
    dropdownColor,
    dropdownDisabled,
    dropdownItems,
  } = config;

  const normalizedType = type || mapColorToAntdType(color);
  const normalizedDropdownType = mapColorToAntdType(dropdownColor);

  return {
    label,
    icon,
    tooltip,
    disabled,
    onClick,
    type: normalizedType,
    hideButton: !!hideButton,
    dropdownIcon,
    dropdownTooltip,
    dropdownType: normalizedDropdownType,
    dropdownDisabled,
    dropdownItems,
    _actionConfig: config,
  };
}

function mapColorToAntdType(color) {
  if (!color) return undefined;
  const c = String(color).toLowerCase();
  if (['primary', 'default', 'dashed', 'link', 'text'].includes(c)) return c;
  return undefined;
}

function safeCall(fn, arg) {
  try {
    return fn?.(arg);
  } catch {
    return null;
  }
}

function deepEqualActions(a, b) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const ai = a[i];
    const bi = b[i];
    // compare by identity for functions, shallow for objects, direct for strings
    if (typeof ai !== typeof bi) return false;
    if (typeof ai === 'function') {
      if (ai !== bi) return false;
    } else if (typeof ai === 'string') {
      if (ai !== bi) return false;
    } else if (typeof ai === 'object') {
      const ak = ai?.action || ai?.name || ai?.type;
      const bk = bi?.action || bi?.name || bi?.type;
      // If both have same action key, need deeper comparison for dropdownMenu
      if (ak && bk && ak === bk) {
        // For dropdownMenu, compare dropdownItems by their keys
        if (ak === 'dropdownMenu') {
          const aiItems = ai?.dropdownItems || [];
          const biItems = bi?.dropdownItems || [];
          if (aiItems.length !== biItems.length) return false;
          for (let j = 0; j < aiItems.length; j++) {
            const aiKey = aiItems[j]?.key || aiItems[j];
            const biKey = biItems[j]?.key || biItems[j];
            if (aiKey !== biKey) return false;
          }
        }
        // Same action key and same items (or not dropdownMenu), continue
      } else if (ak !== bk) {
        // Different action keys
        if (!ak && !bk) {
          // Neither has action key, compare by JSON
          if (JSON.stringify(ai) !== JSON.stringify(bi)) return false;
        } else {
          return false;
        }
      }
    } else {
      if (ai !== bi) return false;
    }
  }
  return true;
}
